import { useEffect, useState } from "react"
import { Box, InputAdornment, MenuItem, Select, TextField, Tooltip } from "@mui/material"
import { If, Else } from "lib/@components/switch"
import { getCurrencySymbol } from "../format-utils"

const MODE_HOURLY = "hourly"
const MODE_FIXED = "fixed-annual"

export function RateCardTradePricing({ item, currency, onUpdateLine, readOnly }) {
    const [hourlyRate, setHourlyRate] = useState(item.hourlyRate ?? 0)
    const [hourlyDisplay, setHourlyDisplay] = useState(formatNumber(item.hourlyRate ?? 0))
    const [fixedAmount, setFixedAmount] = useState(formatNumber(item.fixedAnnualAmount ?? 0))
    const [fixedAmountError, setFixedAmountError] = useState("")
    const [contractedDays, setContractedDays] = useState(formatNumber(item.contractedDaysPerYear))
    const [contractedDaysError, setContractedDaysError] = useState("")
    const mode = item.mode === MODE_FIXED ? MODE_FIXED : MODE_HOURLY
    const symbol = getCurrencySymbol(currency)

    useEffect(syncHourlyRateFromItem, [item.hourlyRate])
    useEffect(syncFixedAmountFromItem, [item.fixedAnnualAmount])
    useEffect(syncContractedDaysFromItem, [item.contractedDaysPerYear])

    return (
        <>
            <Box sx={{ width: 150, flexShrink: 0 }}>
                <Select
                    data-cy="trade-mode"
                    size="small"
                    value={mode}
                    onChange={handleModeChange}
                    disabled={readOnly}
                    inputProps={{ "aria-label": `Rate type for ${item.tradeName || item.tradeId}` }}
                    fullWidth
                    sx={{ "& .MuiSelect-select": { py: 0.75 } }}
                >
                    <MenuItem value={MODE_HOURLY}>Hourly</MenuItem>
                    <MenuItem value={MODE_FIXED}>Fixed annual</MenuItem>
                </Select>
            </Box>
            <Box sx={{ width: 320, flexShrink: 0, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <If value={mode === MODE_FIXED}>
                    <Tooltip title="Contracted days per year (optional)" placement="top">
                        <TextField
                            size="small"
                            data-cy="contracted-days-per-year"
                            value={contractedDays}
                            onChange={handleContractedDaysChange}
                            onBlur={handleContractedDaysBlur}
                            disabled={readOnly}
                            error={Boolean(contractedDaysError)}
                            helperText={contractedDaysError}
                            placeholder="—"
                            inputProps={{ inputMode: "decimal", "aria-label": "Contracted days per year" }}
                            InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                            sx={{ width: 120, "& input": { textAlign: "right", py: 0.75 } }}
                        />
                    </Tooltip>
                </If>
                <Box sx={{ width: 192, flexShrink: 0 }}>
                    <If value={mode === MODE_HOURLY}>
                        <TextField
                            size="small"
                            value={hourlyDisplay}
                            onChange={handleHourlyRateChange}
                            onBlur={handleHourlyRateBlur}
                            disabled={readOnly}
                            inputProps={{
                                inputMode: "decimal",
                                "aria-label": `Hourly rate for ${item.tradeName || item.tradeId}`,
                            }}
                            InputProps={{ startAdornment: <InputAdornment position="start">{symbol}</InputAdornment> }}
                            sx={{ "& input": { textAlign: "right", py: 0.75 } }}
                            fullWidth
                        />
                        <Else>
                            <TextField
                                size="small"
                                data-cy="fixed-annual-amount"
                                value={fixedAmount}
                                onChange={handleFixedAmountChange}
                                onBlur={handleFixedAmountBlur}
                                disabled={readOnly}
                                error={Boolean(fixedAmountError)}
                                helperText={fixedAmountError}
                                placeholder="0"
                                inputProps={{
                                    inputMode: "decimal",
                                    "aria-label": `Fixed annual amount for ${item.tradeName || item.tradeId}`,
                                }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
                                    endAdornment: <InputAdornment position="end">/ yr</InputAdornment>,
                                }}
                                sx={{ "& input": { textAlign: "right", py: 0.75 } }}
                                fullWidth
                            />
                        </Else>
                    </If>
                </Box>
            </Box>
        </>
    )

    function syncHourlyRateFromItem() {
        const nextHourlyRate = item.hourlyRate ?? 0
        setHourlyRate(nextHourlyRate)
        setHourlyDisplay(formatNumber(nextHourlyRate))
    }

    function syncFixedAmountFromItem() {
        setFixedAmount(formatNumber(item.fixedAnnualAmount ?? 0))
        setFixedAmountError("")
    }

    function syncContractedDaysFromItem() {
        setContractedDays(formatNumber(item.contractedDaysPerYear))
        setContractedDaysError("")
    }

    function handleModeChange(event) {
        if (readOnly) return
        const next = event.target.value === MODE_FIXED ? MODE_FIXED : MODE_HOURLY
        onUpdateLine(item.tradeId, "mode", next)
        if (next === MODE_FIXED && !(typeof item.fixedAnnualAmount === "number" && item.fixedAnnualAmount >= 0)) {
            onUpdateLine(item.tradeId, "fixedAnnualAmount", 0)
        }
    }

    function handleHourlyRateChange(event) {
        if (readOnly) return
        const raw = sanitizeNumericInput(event.target.value)
        setHourlyRate(raw)
        setHourlyDisplay(formatNumericInput(raw))
    }

    function handleHourlyRateBlur() {
        if (readOnly) return
        const parsed = Number(String(hourlyRate).replace(/,/g, ""))
        const next = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
        setHourlyRate(next)
        setHourlyDisplay(formatNumber(next))
        onUpdateLine(item.tradeId, "hourlyRate", next)
    }

    function handleFixedAmountChange(event) {
        if (readOnly) return
        setFixedAmount(formatNumericInput(sanitizeNumericInput(event.target.value)))
        setFixedAmountError("")
    }

    function handleFixedAmountBlur() {
        if (readOnly) return
        const parsed = Number(String(fixedAmount).replace(/,/g, ""))
        if (!Number.isFinite(parsed) || parsed < 0) {
            setFixedAmountError("Must be zero or positive")
            return
        }
        setFixedAmountError("")
        setFixedAmount(formatNumber(parsed))
        onUpdateLine(item.tradeId, "fixedAnnualAmount", parsed)
    }

    function handleContractedDaysChange(event) {
        if (readOnly) return
        setContractedDays(sanitizeNumericInput(event.target.value))
        setContractedDaysError("")
    }

    function handleContractedDaysBlur() {
        if (readOnly) return
        if (String(contractedDays).trim() === "") {
            setContractedDaysError("")
            onUpdateLine(item.tradeId, "contractedDaysPerYear", null)
            return
        }
        const parsed = Number(String(contractedDays).replace(/,/g, ""))
        const rounded = Math.round(parsed * 100) / 100
        if (!Number.isFinite(parsed) || !Number.isFinite(rounded) || parsed <= 0 || rounded <= 0) {
            setContractedDaysError("Must be positive")
            return
        }
        setContractedDaysError("")
        setContractedDays(formatNumber(rounded))
        onUpdateLine(item.tradeId, "contractedDaysPerYear", rounded)
    }
}

function sanitizeNumericInput(value) {
    return value.replace(/,/g, "").replace(/[^0-9.]/g, "")
}

function formatNumericInput(value) {
    const [integer, ...decimals] = value.split(".")
    const formattedInteger = integer ? parseInt(integer, 10).toLocaleString("en-GB") : ""
    if (decimals.length === 0) return formattedInteger
    return `${formattedInteger}.${decimals.join(".")}`
}

function formatNumber(value) {
    if (value == null || value === "") return ""
    const parsed = Number(value)
    if (!Number.isFinite(parsed)) return ""
    return parsed.toLocaleString("en-GB", { maximumFractionDigits: 2 })
}
